const domainModel = require('domain-model');
const _ = require('lodash');
_.mixin(require('lodash-uuid'));
const {
  applyPricedArticleToOrderRow,
  removeSpecialRows,
  getOrderSummary,
  ensureInvariants,
  qualifiesAsReturnToSupplierOrder,
  copyOrder,
  isExportOrder,
} = require('domain-model').order;
const moment = require('moment');

const ensureInvariantsAndCalculate = order => {
  const ensuredOrder = ensureInvariants(order);
  return { ...ensuredOrder, calculations: getOrderSummary(ensuredOrder) };
};

const {
  REQUEST,
  SUCCESS,
  FAILURE,
} = require('flaivy-react/reducers/action-type');

const newOrder = () => ({
  id: _.uuid(),
  orderState: 'draft',
  orderRows: [],
  calculations: {},
  deliveryName: null,
  deliveryAddress: '',
  deliveryAddress2: null,
  deliveryCity: null,
  deliveryZipCode: null,
  yourReference: null,
  email: null,
  phone: null,
  deliveryInformation: null,
  remarks: null,
  orderFullfillmentCenter: null,
});

const initialState = () => ({
  loading: false,
  order: newOrder(),
  minimumOrderRequirements: {},
  isCopy: false,
});

const updateOrderRows = (orderRows, updatedRow) => {
  const existingIndex = _.findIndex(orderRows, x => x.id === updatedRow.id);
  if (existingIndex >= 0) {
    orderRows.splice(existingIndex, 1, { ...updatedRow }); // merge
  }
  return orderRows;
};

// eslint-disable-next-line default-param-last
const cartReducer = (state = initialState(), action) => {
  const { type, payload } = action;
  switch (type) {
    case 'CART_NEW_ORDER': {
      const { orderRow } = payload;
      const ensuredOrder = ensureInvariantsAndCalculate({
        ...newOrder(),
        orderRows: orderRow ? [orderRow] : [],
      });

      return {
        ...state,
        loading: false,
        order: ensuredOrder,
        isCopy: false,
      };
    }

    case 'CART_ADD_ORDER_ROW': {
      const { order } = state;

      const { orderRow, supplierCustomer } = payload;
      const orderFullfillmentCenter =
        payload.orderFullfillmentCenter || order.orderFullfillmentCenter;

      if (orderRow.article) {
        applyPricedArticleToOrderRow(orderRow, orderRow.article);
      }
      const newOrderState = {
        ...order,
        orderFullfillmentCenter,
        shippedBy: orderFullfillmentCenter?.name,
        supplierCustomer: order.supplierCustomer || supplierCustomer,
        orderRows: [...order.orderRows, orderRow],
      };

      const ensuredOrder = ensureInvariantsAndCalculate(newOrderState);

      const result = {
        ...state,
        order: ensuredOrder,
      };

      if (orderFullfillmentCenter) {
        result.minimumOrderRequirements =
          domainModel.cart.getMinimumOrderRequirements(ensuredOrder);
      }
      return result;
    }

    case 'CART_UPDATE_ORDER_ROW': {
      const { order } = state;
      const { orderRow } = payload;

      const orderRows = updateOrderRows(order.orderRows, orderRow);

      const newOrderState = { ...order, orderRows };
      const ensuredOrder = ensureInvariantsAndCalculate(newOrderState);
      const result = {
        ...state,
        order: ensuredOrder,
      };

      if (order.orderFullfillmentCenter) {
        result.minimumOrderRequirements =
          domainModel.cart.getMinimumOrderRequirements(ensuredOrder);
      }

      return result;
    }

    case 'CART_CHANGE_ARTICLE': {
      const { order } = state;
      const { article, orderRow } = payload;
      const { orderFullfillmentCenter } = order;
      const hasConflictingCurrencyToCurrentOrderRows = !!order.orderRows.find(
        x => x.article && x.article.currency !== article.currency,
      );
      if (
        hasConflictingCurrencyToCurrentOrderRows &&
        order.orderRows.length > 1
      ) {
        throw new Error(
          'order is not allowed to contain articles with different currencies',
          412,
          'PRECONDITION_FAILED',
        );
      }

      const nrOfUnitsPerorderUnits =
        domainModel.article.getMinOrderableQuantityIncrement(article);

      orderRow.orderedQuantity = nrOfUnitsPerorderUnits;
      orderRow.deliveredQuantity = nrOfUnitsPerorderUnits;

      if (article) {
        applyPricedArticleToOrderRow(orderRow, article);
      }
      const orderRows = updateOrderRows(order.orderRows, orderRow);

      const newOrderState = { ...order, orderRows };
      const ensuredOrder = ensureInvariantsAndCalculate(newOrderState);
      const result = {
        ...state,
        order: ensuredOrder,
      };
      if (orderFullfillmentCenter) {
        result.minimumOrderRequirements =
          domainModel.cart.getMinimumOrderRequirements(ensuredOrder);
      }
      return result;
    }

    case 'CART_REMOVE_ORDER_ROW': {
      const { orderRow } = payload;
      const orderRows = state.order.orderRows.filter(x => x.id !== orderRow.id);
      const order = { ...state.order, orderRows };
      const ensuredOrder = ensureInvariantsAndCalculate(order);
      const result = {
        ...state,
        order: ensuredOrder,
      };
      if (order.orderFullfillmentCenter) {
        result.minimumOrderRequirements =
          domainModel.cart.getMinimumOrderRequirements(ensuredOrder);
      }
      return result;
    }

    case 'CART_CLEAR_ORDER_ROWS': {
      const newOrderState = { ...state.order, orderRows: [] };
      const ensuredOrder = ensureInvariantsAndCalculate(newOrderState);
      return {
        ...state,
        order: ensuredOrder,
        minimumOrderRequirements: {},
      };
    }

    case SUCCESS('CART_LOAD_ORDER'): {
      const { order } = payload;
      const { orderFullfillmentCenter } = order;
      const orderRowsWithoutSpecialRows = removeSpecialRows(order.orderRows);
      const loadedOrder = {
        ...newOrder(),
        ...order,
        orderRows: orderRowsWithoutSpecialRows,
        numberOfArticlesOnOrder: orderRowsWithoutSpecialRows.length,
        orderFullfillmentCenter,
      };
      const result = {
        ...state,
        error: false,
        loading: false,
        order: loadedOrder,
        isCopy: false,
      };
      if (orderFullfillmentCenter) {
        result.minimumOrderRequirements =
          domainModel.cart.getMinimumOrderRequirements(loadedOrder);
      }
      return result;
    }

    case REQUEST('CART_LOAD_ORDER'): {
      return { ...state, loading: true };
    }

    case FAILURE('CART_LOAD_ORDER'): {
      return {
        ...state,
        loading: false,
        error: {
          code: payload.response.data.code,
          message: payload.response.data.message,
        },
      };
    }

    case 'CART_CHANGE_CUSTOMER': {
      const {
        pricedArticles,
        customerSubUnit,
        supplierCustomer,
        orderReferenceDetails,
        orderFullfillmentCenter: newOrderFullfillmentCenter,
      } = payload;

      const { orderRows } = state.order;

      const orderFullfillmentCenter =
        newOrderFullfillmentCenter || state.order.orderFullfillmentCenter;

      const orderRowsWithOutSpecialRows = removeSpecialRows(orderRows);
      if (pricedArticles) {
        orderRowsWithOutSpecialRows
          .filter(row => row.article)
          .forEach(x => {
            const article = pricedArticles.find(
              a => a.articleNumber === x.article.articleNumber,
            );
            if (article) {
              applyPricedArticleToOrderRow(x, article);
            }
          });
      }

      const orderToEnsure = {
        ...state.order,
        customerSubUnit,
        supplierCustomer,
        orderFullfillmentCenter,
        orderRows: orderRowsWithOutSpecialRows,
      };
      let orderOrigin = 'supplier';

      if (isExportOrder(orderToEnsure)) {
        orderOrigin = 'export';
      }
      if (qualifiesAsReturnToSupplierOrder(orderToEnsure)) {
        orderOrigin = 'returnToSupplier';
      }

      const ensuredOrder = ensureInvariantsAndCalculate({
        ...orderToEnsure,
        orderOrigin,
      });

      const order = {
        ...ensuredOrder,
        ...orderReferenceDetails,
        orderRows: ensuredOrder.orderRows,
      };

      const deliveryRulesCalculator =
        new domainModel.deliveryRules.DeliveryRulesCalculator(
          order.orderFullfillmentCenter,
          order.customerSubUnit,
          order.supplierCustomer,
          order.supplierConnection,
        );

      const firstPossibleDeliveryDate =
        deliveryRulesCalculator.getEarliestPossibleDeliveryDate();
      if (
        order.deliveryDate &&
        moment(order.deliveryDate).isBefore(firstPossibleDeliveryDate)
      ) {
        order.deliveryDate = firstPossibleDeliveryDate;
      }
      const result = {
        ...state,
        order,
      };
      if (orderFullfillmentCenter) {
        result.minimumOrderRequirements =
          domainModel.cart.getMinimumOrderRequirements(result.order);
      }
      return result;
    }
    case 'CART_CHANGE_SINGLE_ORDER_FIELD':
    case 'CART_CHANGE_ORDER_FIELD': {
      const { field, value } = payload;
      const { order } = state;
      const orderToEnsure = {
        ...order,
        [field]: value,
      };
      const ensuredOrder = ensureInvariantsAndCalculate(orderToEnsure);

      const result = { ...state, order: ensuredOrder };

      if (ensuredOrder.orderFullfillmentCenter) {
        result.minimumOrderRequirements =
          domainModel.cart.getMinimumOrderRequirements(result.order);
      }
      return result;
    }

    case 'CART_CHANGE_CONTACT': {
      const { contact } = payload;
      return {
        ...state,
        order: {
          ...state.order,
          yourReference: contact.name,
          phone: contact.phone,
          email: contact.email,
        },
      };
    }

    case SUCCESS('CART_COPY_ORDER'): {
      const { order } = payload;

      const newOrderState = copyOrder(order);

      const ensuredOrder = ensureInvariantsAndCalculate(newOrderState);

      const result = {
        ...state,
        isCopy: true,
        error: null,
        order: ensuredOrder,
      };
      if (order.orderFullfillmentCenter) {
        result.minimumOrderRequirements =
          domainModel.cart.getMinimumOrderRequirements(ensuredOrder);
      }
      return result;
    }

    case REQUEST('CART_COPY_ORDER'): {
      return { ...state, loading: true };
    }

    case FAILURE('CART_COPY_ORDER'): {
      return {
        ...state,
        loading: false,
        error: {
          code: _.get(payload, 'response.data.code'),
          message: _.get(payload, 'response.data.message', payload),
        },
      };
    }

    default:
      return state;
  }
};

module.exports = cartReducer;
